import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/auth.store';
import { useBranchStore } from '../../stores/branch.store';
import { useCartStore } from '../../stores/cart.store';
import { useCategories, useMenuItems } from '../../api/queries/menu.queries';
import DrinkCard from './components/DrinkCard';
import CartPanel from './components/CartPanel';
import DrinkDetailModal from './components/DrinkDetailModal';
import CheckoutModal from './components/CheckoutModal';
import POSLayout from '../../components/layouts/POSLayout';
import { MenuItem } from '../../api/queries/menu.queries';
import {
  Search,
  Mic,
  MicOff,
  ChevronLeft,
  ChevronRight,
  Grid3X3,
  Sparkles,
  Building2,
} from 'lucide-react';

// Category icons mapping
const categoryIcons: Record<string, string> = {
  recommended: '⭐',
  'fruit-tea': '🍊',
  'milk-tea': '🥛',
  'thai-tea': '🇹🇭',
  matcha: '🍵',
  signature: '✨',
  coffee: '☕',
  smoothie: '🧊',
  default: '🥤',
};

function POSPage() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const selectedBranchId = useBranchStore((state) => state.selectedBranchId);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedDrink, setSelectedDrink] = useState<MenuItem | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const cartItems = useCartStore((state) => state.items);
  const recognitionRef = useRef<any>(null);

  // Use selected branch from header dropdown, fallback to user's branch
  const branchId = selectedBranchId || user?.branchId;

  // Helper function - defined early so it can be used in filtering
  const getLangValue = (multiLang: any) => {
    return multiLang?.[i18n.language as 'en' | 'th'] || multiLang?.en || '';
  };

  const { data: categories } = useCategories();
  const { data: menuItems, isLoading: itemsLoading } = useMenuItems(
    selectedCategory || undefined,
    branchId
  );

  // Filter menu items by search query
  const filteredItems = menuItems?.filter((item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const name = getLangValue(item.name).toLowerCase();
    const tags = item.tags?.join(' ').toLowerCase() || '';
    return name.includes(query) || tags.includes(query);
  });

  // Voice search setup
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = i18n.language === 'th' ? 'th-TH' : 'en-US';

      recognitionRef.current.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setSearchQuery(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = () => {
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, [i18n.language]);

  const toggleVoiceSearch = () => {
    if (!recognitionRef.current) return;

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.lang = i18n.language === 'th' ? 'th-TH' : 'en-US';
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const handleDrinkClick = (drink: MenuItem) => {
    setSelectedDrink(drink);
  };

  const handleCloseDrinkModal = () => {
    setSelectedDrink(null);
  };

  const handleCheckout = () => {
    if (cartItems.length > 0) {
      setShowCheckout(true);
    }
  };

  const getCategoryIcon = (categoryName: string) => {
    const name = categoryName.toLowerCase();
    if (name.includes('recommend')) return categoryIcons.recommended;
    if (name.includes('fruit')) return categoryIcons['fruit-tea'];
    if (name.includes('milk')) return categoryIcons['milk-tea'];
    if (name.includes('thai')) return categoryIcons['thai-tea'];
    if (name.includes('matcha')) return categoryIcons.matcha;
    if (name.includes('signature')) return categoryIcons.signature;
    if (name.includes('coffee')) return categoryIcons.coffee;
    if (name.includes('smoothie')) return categoryIcons.smoothie;
    return categoryIcons.default;
  };

  return (
    <POSLayout>
      <div className="flex h-full">
        {/* Left Sidebar - Categories */}
        <aside
          className={`${
            sidebarCollapsed ? 'w-16' : 'w-56'
          } border-r bg-card flex flex-col shrink-0 transition-all duration-200`}
        >
          {/* Sidebar Header */}
          <div className="p-3 border-b flex items-center justify-between">
            {!sidebarCollapsed && (
              <h2 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                {t('pos.categories') || 'Categories'}
              </h2>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-1.5 rounded-lg hover:bg-accent transition-colors"
            >
              {sidebarCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </button>
          </div>

          {/* Category List */}
          <nav className="flex-1 overflow-y-auto p-2">
            <ul className="space-y-1">
              {/* All Items */}
              <li>
                <button
                  onClick={() => setSelectedCategory('')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors touch-target tap-highlight-none ${
                    selectedCategory === ''
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-accent text-foreground'
                  }`}
                >
                  <span className="text-lg flex-shrink-0">
                    <Grid3X3 className="h-5 w-5" />
                  </span>
                  {!sidebarCollapsed && (
                    <span className="font-medium text-sm truncate">
                      {t('common.all') || 'All Items'}
                    </span>
                  )}
                </button>
              </li>

              {/* Category Items */}
              {categories?.map((category) => (
                <li key={category._id}>
                  <button
                    onClick={() => setSelectedCategory(category._id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors touch-target tap-highlight-none ${
                      selectedCategory === category._id
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-accent text-foreground'
                    }`}
                  >
                    <span className="text-lg flex-shrink-0">
                      {category.imageUrl ? (
                        <img
                          src={category.imageUrl}
                          alt=""
                          className="w-5 h-5 rounded object-cover"
                        />
                      ) : (
                        getCategoryIcon(getLangValue(category.name))
                      )}
                    </span>
                    {!sidebarCollapsed && (
                      <span className="font-medium text-sm truncate">
                        {getLangValue(category.name)}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* Main Content - Menu Grid */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Search Bar */}
          <div className="p-4 border-b bg-card">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('pos.searchMenu') || 'Search menu...'}
                className="w-full pl-10 pr-12 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
              {/* Voice Search Button */}
              <button
                onClick={toggleVoiceSearch}
                className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-colors ${
                  isListening
                    ? 'bg-destructive text-destructive-foreground animate-pulse'
                    : 'hover:bg-accent text-muted-foreground'
                }`}
                title={isListening ? 'Stop listening' : 'Voice search'}
              >
                {isListening ? (
                  <MicOff className="h-5 w-5" />
                ) : (
                  <Mic className="h-5 w-5" />
                )}
              </button>
            </div>
            {/* Search hint */}
            {isListening && (
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <span className="inline-block w-2 h-2 bg-destructive rounded-full animate-pulse" />
                {t('pos.listening') || 'Listening...'}
              </p>
            )}
          </div>

          {/* Menu Grid */}
          <div className="flex-1 overflow-y-auto p-4 bg-muted/30">
            {itemsLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent mb-2" />
                  <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
                </div>
              </div>
            ) : !branchId ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Building2 className="h-16 w-16 text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t('pos.selectBranch') || 'Select a Branch'}</h3>
                <p className="text-muted-foreground max-w-md">
                  {t('pos.selectBranchDesc') || 'Please select a branch from the dropdown above to start taking orders.'}
                </p>
              </div>
            ) : filteredItems && filteredItems.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
                {filteredItems.map((item) => (
                  <DrinkCard
                    key={item._id}
                    drink={item}
                    onClick={() => handleDrinkClick(item)}
                    getLangValue={getLangValue}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Sparkles className="h-12 w-12 text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">
                  {searchQuery
                    ? t('pos.noSearchResults') || 'No items match your search'
                    : t('pos.noItems') || 'No items in this category'}
                </p>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="mt-2 text-sm text-primary hover:underline"
                  >
                    {t('common.clearSearch') || 'Clear search'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - Cart */}
        <CartPanel onCheckout={handleCheckout} />

        {/* Modals */}
        {selectedDrink && (
          <DrinkDetailModal
            drink={selectedDrink}
            onClose={handleCloseDrinkModal}
            getLangValue={getLangValue}
          />
        )}

        {showCheckout && (
          <CheckoutModal onClose={() => setShowCheckout(false)} />
        )}
      </div>
    </POSLayout>
  );
}

export default POSPage;
